import { Link, NavLink } from 'react-router-dom';
import { useMediaQuery, useSeason, useSeasonArt } from './context';

// The slots the header and footer render. Each draws nothing unless a season
// is running, so the default site carries no trace of them.

/** The season's entry link (an event page), styled for the header bar. */
export function SeasonalEntry({
  variant,
  onNavigate,
  className = '',
}: {
  variant: 'bar' | 'menu';
  onNavigate?: () => void;
  className?: string;
}) {
  const { season } = useSeason();
  const entry = season?.entry;
  if (!entry) return null;

  if (variant === 'menu') {
    return (
      <NavLink
        to={entry.to}
        onClick={onNavigate}
        title={entry.title}
        className={({ isActive }) =>
          `sn-season-entry sn-season-entry-menu flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
            isActive ? 'bg-[var(--color-surface)] text-textPrimary' : 'text-textSecondary hover:bg-white/[0.03] hover:text-textPrimary'
          }`
        }
      >
        <EntryMark />
        <span className="sn-season-entry-label">{entry.label}</span>
      </NavLink>
    );
  }

  return <EntryBar entry={entry} className={className} />;
}

/** The header-bar entry; fetches its emblem only where the bar link shows. */
function EntryBar({ entry, className }: { entry: { to: string; label: string; title?: string }; className: string }) {
  const shown = useMediaQuery('(min-width: 1024px)');

  return (
    <NavLink
      to={entry.to}
      title={entry.title}
      className={({ isActive }) =>
        `sn-season-entry sn-season-entry-bar ${className || 'flex'} mr-1 items-center gap-2 rounded-md px-2.5 py-1 text-[13px] transition-colors ${
          isActive ? 'text-textPrimary bg-[var(--color-surface)]' : 'text-textSecondary hover:text-textPrimary hover:bg-white/[0.03]'
        }`
      }
    >
      <EntryMark enabled={shown} />
      <span className="sn-season-entry-label">{entry.label}</span>
    </NavLink>
  );
}

/**
 * The entry's emblem: the season's painted mark (`entry-mark`, with an
 * optional moving `entry-mark-part` the look can animate) or, without art, a
 * small accent dot. Sources are full-resolution art the browser scales. The box is a fixed size, so the art arriving never moves
 * the label.
 */
/** 1x/2x/3x for the exported 22 px emblem (`*-44.webp` is the 2x file). */
function markSrcSet(src: string): string | undefined {
  const m = /^(.*)-44\.webp$/.exec(src);
  return m ? `${m[1]}-22.webp 1x, ${m[1]}-44.webp 2x, ${m[1]}-66.webp 3x` : undefined;
}

function EntryMark({ enabled = true }: { enabled?: boolean }) {
  const base = useSeasonArt('entry-mark', enabled);
  const part = useSeasonArt('entry-mark-part', enabled);
  return (
    <span aria-hidden="true" className="sn-season-entry-mark relative inline-flex h-[22px] w-[17px] flex-shrink-0 items-center justify-center">
      {base ? (
        <>
          <img src={base} srcSet={markSrcSet(base)} alt="" className="sn-season-entry-mark-base absolute inset-0 h-full w-full object-contain" />
          {part && <img src={part} srcSet={markSrcSet(part)} alt="" className="sn-season-entry-mark-part absolute inset-0 h-full w-full object-contain" />}
        </>
      ) : (
        <span className="sn-season-entry-dot relative h-1.5 w-1.5 rounded-full bg-accent" />
      )}
    </span>
  );
}

/**
 * The footer's seasonal row: the entry link and the visitor's on/off switch.
 * The switch stays while the season runs, so turning it off can be undone.
 */
export function SeasonalFooterControls() {
  const { available, season, effectsOn, setEffectsOn } = useSeason();
  if (!available) return null;
  const entry = season?.entry;

  return (
    <span className="mb-3 flex items-center gap-5 sm:float-right sm:mb-0 sm:ml-6 sm:mt-[3px] sm:inline-flex">
      {entry && (
        <Link to={entry.to} className="text-xs text-textMuted transition-colors hover:text-textPrimary">
          {entry.label}
        </Link>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={effectsOn}
        onClick={() => setEffectsOn(!effectsOn)}
        title={`${available.label} look for the site`}
        className="group inline-flex items-center gap-2 text-xs text-textMuted transition-colors hover:text-textPrimary"
      >
        Seasonal effects
        <span
          aria-hidden="true"
          className={`relative inline-block h-[14px] w-[24px] rounded-full transition-colors ${
            effectsOn ? 'bg-accent/70' : 'bg-white/[0.12]'
          }`}
        >
          <span
            className={`absolute top-[2px] left-[2px] h-[10px] w-[10px] rounded-full bg-white transition-transform ${
              effectsOn ? 'translate-x-[10px]' : 'translate-x-0'
            }`}
          />
        </span>
      </button>
    </span>
  );
}
